import React, { useCallback, useMemo, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNodeByKey, type NodeKey } from 'lexical';

import SlotEditorPlugin from '../plugins/SlotEditorPlugin';
import type { SerializedSubstate } from '../../utils/mathTypes';
import type { OverlineVariant } from './MathNodes';

/* ===================== Utilities ===================== */

// Calculate nesting depth by traversing up the DOM
const calculateDepth = (
    editor: ReturnType<typeof useLexicalComposerContext>[0],
    nodeKey: NodeKey
): number => {
    let depth = 0;
    try {
        const dom = editor.getElementByKey(nodeKey);
        if (!dom) return depth;

        // Count .pm-math ancestors (each math node adds one level)
        let current = dom.parentElement;
        while (current) {
            if (current.classList.contains('pm-math')) {
                depth++;
            }
            current = current.parentElement;
        }
    } catch {
        // If we can't determine depth, return 0 (allow insertion)
    }
    return depth;
};

const focusEditable = (ref: React.RefObject<HTMLElement | null>) => {
    const root = ref.current;
    if (!root) return;
    const editable = root.querySelector<HTMLElement>('[contenteditable="true"]');
    editable?.focus();
};

const makeEscapeLeft =
    (editor: ReturnType<typeof useLexicalComposerContext>[0], nodeKey: NodeKey) => () => {
        editor.update(() => {
            const dom = editor.getElementByKey(nodeKey);
            if (!dom) return;
            const range = document.createRange();
            range.setStartBefore(dom);
            range.collapse(true);
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(range);
            }
        });
    };

const makeEscapeRight =
    (editor: ReturnType<typeof useLexicalComposerContext>[0], nodeKey: NodeKey) => () => {
        editor.update(() => {
            const dom = editor.getElementByKey(nodeKey);
            if (!dom) return;
            const range = document.createRange();
            range.setStartAfter(dom);
            range.collapse(true);
            const sel = window.getSelection();
            if (sel) {
                sel.removeAllRanges();
                sel.addRange(range);
            }
        });
    };

/* ===================== FRACTION ===================== */
export function FractionView({
    nodeKey,
    num,
    den,
}: {
    nodeKey: NodeKey;
    num: SerializedSubstate;
    den: SerializedSubstate;
}) {
    const [editor] = useLexicalComposerContext();

    const setNum = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'fraction') {
                    const w = node.getWritable();
                    w.__num = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const setDen = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'fraction') {
                    const w = node.getWritable();
                    w.__den = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const numRef = useRef<HTMLSpanElement | null>(null);
    const denRef = useRef<HTMLSpanElement | null>(null);

    const escapeLeft = useMemo(() => makeEscapeLeft(editor, nodeKey), [editor, nodeKey]);
    const escapeRight = useMemo(() => makeEscapeRight(editor, nodeKey), [editor, nodeKey]);

    const currentDepth = useMemo(() => calculateDepth(editor, nodeKey), [editor, nodeKey]);

    return (
        <span className="pm-math-frac-ui" data-math="frac" contentEditable={false}>
            <span ref={numRef}>
                <SlotEditorPlugin
                    substate={num}
                    onChange={setNum}
                    className="pm-math-num"
                    depth={currentDepth + 1}
                    onTabNext={() => focusEditable(denRef)}
                    onEscapeLeft={escapeLeft}
                />
            </span>
            <span className="pm-math-bar" aria-hidden="true" />
            <span ref={denRef}>
                <SlotEditorPlugin
                    substate={den}
                    onChange={setDen}
                    className="pm-math-den"
                    depth={currentDepth + 1}
                    onTabPrev={() => focusEditable(numRef)}
                    onEscapeRight={escapeRight}
                />
            </span>
        </span>
    );
}

/* ===================== EXPONENT ===================== */
export function ExponentView({
    nodeKey,
    base,
    exp,
}: {
    nodeKey: NodeKey;
    base: SerializedSubstate;
    exp: SerializedSubstate;
}) {
    const [editor] = useLexicalComposerContext();

    const setBase = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'exponent') {
                    const w = node.getWritable();
                    w.__base = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const setExp = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'exponent') {
                    const w = node.getWritable();
                    w.__exp = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const baseRef = useRef<HTMLSpanElement | null>(null);
    const expRef = useRef<HTMLSpanElement | null>(null);

    const escapeLeft = useMemo(() => makeEscapeLeft(editor, nodeKey), [editor, nodeKey]);
    const escapeRight = useMemo(() => makeEscapeRight(editor, nodeKey), [editor, nodeKey]);

    const currentDepth = useMemo(() => calculateDepth(editor, nodeKey), [editor, nodeKey]);

    return (
        <span className="pm-math-exp-ui" data-math="exp" contentEditable={false}>
            <span ref={baseRef}>
                <SlotEditorPlugin
                    substate={base}
                    onChange={setBase}
                    className="pm-math-base"
                    depth={currentDepth + 1}
                    onTabNext={() => focusEditable(expRef)}
                    onEscapeLeft={escapeLeft}
                />
            </span>
            <span ref={expRef}>
                <SlotEditorPlugin
                    substate={exp}
                    onChange={setExp}
                    className="pm-math-sup"
                    depth={currentDepth + 1}
                    onTabPrev={() => focusEditable(baseRef)}
                    onEscapeRight={escapeRight}
                />
            </span>
        </span>
    );
}

/* ===================== ROOT ===================== */
export function RootView({
    nodeKey,
    radicand,
    index,
}: {
    nodeKey: NodeKey;
    radicand: SerializedSubstate;
    index: SerializedSubstate | null;
}) {
    const [editor] = useLexicalComposerContext();

    const setRad = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'root') {
                    const w = node.getWritable();
                    w.__radicand = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const setIdx = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'root') {
                    const w = node.getWritable();
                    w.__index = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const idxRef = useRef<HTMLSpanElement | null>(null);
    const radRef = useRef<HTMLSpanElement | null>(null);

    const escapeLeft = useMemo(() => makeEscapeLeft(editor, nodeKey), [editor, nodeKey]);
    const escapeRight = useMemo(() => makeEscapeRight(editor, nodeKey), [editor, nodeKey]);

    const currentDepth = useMemo(() => calculateDepth(editor, nodeKey), [editor, nodeKey]);

    const hasIndex = index != null;
    const variant = hasIndex ? 'nth' : 'sqrt';

    return (
        <span className="pm-math-root-ui" data-root-variant={variant} contentEditable={false}>
            {hasIndex ? (
                <span ref={idxRef}>
                    <SlotEditorPlugin
                        substate={index as SerializedSubstate}
                        onChange={setIdx}
                        className="pm-math-index"
                        depth={currentDepth + 1}
                        onTabNext={() => focusEditable(radRef)}
                        onEscapeLeft={escapeLeft}
                    />
                </span>
            ) : null}
            <span className="pm-math-sqrt-symbol" aria-hidden="true">
                √
            </span>
            <span ref={radRef}>
                <SlotEditorPlugin
                    substate={radicand}
                    onChange={setRad}
                    className="pm-math-radicand"
                    depth={currentDepth + 1}
                    onTabPrev={hasIndex ? () => focusEditable(idxRef) : undefined}
                    onEscapeLeft={hasIndex ? () => focusEditable(idxRef) : escapeLeft}
                    onEscapeRight={escapeRight}
                />
            </span>
        </span>
    );
}

/* ===================== OVERLINE VIEW ===================== */

export function OverlineView({
    nodeKey,
    content,
    variant,
}: {
    nodeKey: NodeKey;
    content: SerializedSubstate;
    variant: OverlineVariant;
}) {
    const [editor] = useLexicalComposerContext();

    const setContent = useCallback(
        (state: string) => {
            editor.update(() => {
                const node: any = $getNodeByKey(nodeKey);
                if (node && node.getType?.() === 'overline') {
                    const w = node.getWritable();
                    w.__content = { state };
                }
            });
        },
        [editor, nodeKey]
    );

    const contentRef = useRef<HTMLSpanElement | null>(null);

    const escapeLeft = useMemo(() => makeEscapeLeft(editor, nodeKey), [editor, nodeKey]);
    const escapeRight = useMemo(() => makeEscapeRight(editor, nodeKey), [editor, nodeKey]);

    const currentDepth = useMemo(() => calculateDepth(editor, nodeKey), [editor, nodeKey]);

    return (
        <span
            className="pm-math-overline-ui"
            data-overline-variant={variant}
            contentEditable={false}
        >
            <span ref={contentRef} className="pm-math-overline-container">
                <SlotEditorPlugin
                    substate={content}
                    onChange={setContent}
                    className="pm-math-overline"
                    depth={currentDepth + 1}
                    onEscapeLeft={escapeLeft}
                    onEscapeRight={escapeRight}
                />
            </span>
        </span>
    );
}
